// recovery-ticket-materialize-try.ts
//
// Orchestrator recovery (3.40.0): ONE try of writing a recovery ticket
// (op id gốc or `-r<n>`). Write order (B18): intent{ticket_id} → ticket file +
// git commit (PLAN.md rebuilt in the same commit) → result applied + todo.
// Resume (intent without result) keeps `params.ticket_id` and always rewrites
// + commits (idempotent: identical bytes ⇒ `no_change`). An id taken by a
// ticket that is not this recovery (`sources` differ) ⇒ `failed`, the next try
// gets a fresh id. Any throw / policy denial after the intent ⇒ clean the two
// paths FIRST, then journal `failed` (D2.5).
import { join } from "node:path";
import { PLAN_INDEX_PATH } from "./controller-plan-index-markdown.js";
import { commitRedirectTicketFiles } from "./directive-redirect-ticket-commit.js";
import type { RecoveryOpRecord } from "./recovery-guards.js";
import {
  commitRecoveryTicketApplied,
  commitRecoveryTicketFailed,
  commitRecoveryTicketIntent,
  type RecoveryTriggerRef,
} from "./recovery-op-journal.js";
import { discardRecoveryAttemptFiles } from "./recovery-ticket-attempt-cleanup.js";
import type { RecoveryMaterializeContext } from "./recovery-ticket-materializer-types.js";
import {
  buildRecoveryTicket,
  isRecoveryTicketFor,
  type RecoveryTicketTrigger,
  recoverySourceLabel,
  recoveryTicketOpId,
  recoveryTicketTitle,
} from "./recovery-ticket-shape.js";
import { ticketContentSha256 } from "./ticket-content-sha256.js";
import { ticketFileName } from "./ticket-file-name.js";
import { nextTicketId, writeTicketFile } from "./ticket-file-persistence.js";
import { loadTicketsAtHead } from "./ticket-files-at-head.js";

export interface RecoveryTryRequest {
  ref: RecoveryTriggerRef & { trigger: RecoveryTicketTrigger };
  attempt: number;
  /** Intent without result to resume (its op id + ticket id are kept). */
  resume?: RecoveryOpRecord;
  /** Ticket id of the previous failed try — reused unless another ticket took it. */
  priorTicketId?: string;
  /** Brief text (recovery-ticket-brief-builder.ts). */
  outcome: string;
}

export type RecoveryTryResult =
  | { status: "applied"; ticketId: string }
  | { status: "failed"; reuseTicketId?: string };

export async function tryMaterializeRecoveryTicket(
  ctx: RecoveryMaterializeContext,
  req: RecoveryTryRequest,
): Promise<RecoveryTryResult> {
  const { ref } = req;
  const label = recoverySourceLabel(ref.source_ticket, ref.trigger);
  const opId =
    req.resume?.operation_id ??
    recoveryTicketOpId(ref.source_ticket, ref.trigger, ref.trigger_seq, req.attempt);
  let intentCommitted = req.resume !== undefined;
  let ticketId = req.resume?.ticket_id;
  try {
    // Reloaded right before choosing an id: sees a ticket the drain just committed (N22).
    // HEAD, not the worktree: the stopped worker's leftovers are reset after us (review G2 M2).
    const tickets = await loadTicketsAtHead(ctx.gitRunner, ctx.repoDir);
    if (req.resume !== undefined) {
      const onDisk = tickets.find((t) => t.id === ticketId);
      if (ticketId === undefined || (onDisk !== undefined && !isRecoveryTicketFor(onDisk, label))) {
        // Id taken by another ticket: never touch its file — fail this op, new id next try.
        await commitRecoveryTicketFailed(ctx.store, opId);
        return { status: "failed" };
      }
    } else {
      const prior = tickets.find((t) => t.id === req.priorTicketId);
      const reusable =
        req.priorTicketId !== undefined &&
        (prior === undefined || isRecoveryTicketFor(prior, label));
      ticketId = reusable ? (req.priorTicketId as string) : nextTicketId(tickets);
      await commitRecoveryTicketIntent(ctx.store, opId, ref, ticketId);
      intentCommitted = true;
    }
    const id = ticketId as string;
    const ticket = buildRecoveryTicket({
      id,
      sourceTicket: ref.source_ticket,
      trigger: ref.trigger,
      outcome: req.outcome,
    });
    const { removed } = await writeTicketFile(ctx.repoDir, ticket);
    const committed = await commitRedirectTicketFiles({
      store: ctx.store,
      repoDir: ctx.repoDir,
      gitRunner: ctx.gitRunner,
      evaluatePolicy: ctx.evaluatePolicy,
      missionId: ctx.missionId,
      directiveId: opId,
      tickets: [ticket],
      removedPaths: removed,
      planIndex: {
        tickets: [...tickets.filter((t) => t.id !== id), ticket],
        status: { ...ctx.store.state.tickets, [id]: "todo" },
      },
      subject: `recovery ${ref.source_ticket} -> ${id}`,
    });
    if (!committed.ok) throw new Error(committed.reason);
    await commitRecoveryTicketApplied(ctx.store, opId, id, ticketContentSha256(ticket));
    return { status: "applied", ticketId: id };
  } catch {
    if (intentCommitted && ticketId !== undefined) {
      const title = recoveryTicketTitle(ref.source_ticket, ref.trigger);
      const ticketPath = join(".avatar", "tickets", ticketFileName({ id: ticketId, title }));
      await discardRecoveryAttemptFiles(ctx.gitRunner, ctx.repoDir, [ticketPath, PLAN_INDEX_PATH]);
      await commitRecoveryTicketFailed(ctx.store, opId);
    }
    return { status: "failed", ...(ticketId === undefined ? {} : { reuseTicketId: ticketId }) };
  }
}
